"use client";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ContactProps } from "@/types/sections/contact";
import { useState, useEffect } from "react";
import { MapPin, Phone, Mail, Clock, Globe } from "lucide-react";

type FormState = Record<string, string>;

export default function ContactUsSectionSimple({
  data,
}: {
  data: ContactProps;
}) {
  const { title, description, form, contactInfo } = data;

  const [formData, setFormData] = useState<FormState>({});
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");

  useEffect(() => {
    if (form?.fields) {
      const initial = Object.keys(form.fields).reduce((acc, key) => {
        acc[key] = "";
        return acc;
      }, {} as FormState);
      setFormData(initial);
    }
  }, [form]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (form?.endpoint) {
        await fetch(form.endpoint, {
          method: form.method || "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });
      } else {
        console.log("Form submission fallback:", formData);
      }
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="max-w-4xl mx-auto px-4 py-10 space-y-8">
      {(title || description) && (
        <div className="text-center space-y-2">
          {title && <h2 className="text-3xl font-bold">{title}</h2>}
          {description && (
            <p className="text-muted-foreground">{description}</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          {form?.fields?.name && (
            <div className="space-y-3">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                name="name"
                value={formData.name || ""}
                onChange={handleChange}
                required
              />
            </div>
          )}
          {form?.fields?.email && (
            <div className="space-y-3">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email || ""}
                onChange={handleChange}
                required
              />
            </div>
          )}
          {form?.fields?.phone && (
            <div className="space-y-3">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone || ""}
                onChange={handleChange}
              />
            </div>
          )}
          {form?.fields?.message && (
            <div className="space-y-3">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                name="message"
                rows={5}
                value={formData.message || ""}
                onChange={handleChange}
                required
              />
            </div>
          )}

          <Button type="submit" className="w-full">
            {form?.submitLabel || "Send Message"}
          </Button>

          {status === "success" && (
            <p className="text-green-600 text-sm">
              {form?.successMessage || "Message sent!"}
            </p>
          )}
          {status === "error" && (
            <p className="text-red-600 text-sm">
              {form?.errorMessage || "Something went wrong."}
            </p>
          )}
        </form>

        {contactInfo && (
          <div className="space-y-5 text-sm text-muted-foreground">
            {contactInfo.address && (
              <div className="flex gap-3">
                <MapPin className="text-primary mt-1" size={24} />
                <div>
                  <p className="font-medium text-foreground">Address</p>
                  <p>{contactInfo.address}</p>
                </div>
              </div>
            )}
            {contactInfo.phone && (
              <div className="flex gap-3">
                <Phone className="text-primary mt-1" size={24} />
                <div>
                  <p className="font-medium text-foreground">Phone</p>
                  <p>{contactInfo.phone}</p>
                </div>
              </div>
            )}
            {contactInfo.email && (
              <div className="flex gap-3">
                <Mail className="text-primary mt-1" size={24} />
                <div>
                  <p className="font-medium text-foreground">Email</p>
                  <p>{contactInfo.email}</p>
                </div>
              </div>
            )}
            {contactInfo.hours && (
              <div className="flex gap-3">
                <Clock className="text-primary mt-1" size={24} />
                <div>
                  <p className="font-medium text-foreground">Hours</p>
                  <p>{contactInfo.hours}</p>
                </div>
              </div>
            )}
            {Array.isArray(contactInfo.socials) &&
              contactInfo.socials.length > 0 && (
                <div className="flex gap-3">
                  <Globe className="text-primary mt-1" size={24} />
                  <div>
                    <p className="font-medium text-foreground">Socials</p>
                    <div className="inline-flex flex-wrap items-center gap-x-4 gap-y-2 mt-2">
                      {contactInfo.socials.map((s, i) => (
                        <a
                          href={s.url}
                          key={i}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {s.platform}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              )}
          </div>
        )}
      </div>
    </section>
  );
}
